import { Link } from "react-router-dom";
import { ArrowRight, Clock, Tag } from "lucide-react";
import { BLOG_POSTS } from "@/lib/blogData";
import SEOHead from "@/components/SEOHead";

const BLOG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "Hylas Companion Driver Resources",
  "description": "Trucking tips, pay breakdowns, and CDL-A driver guides from Hylas Companion.",
  "url": "https://hylascompanion.com/blog",
  "publisher": {
    "@type": "Organization",
    "name": "Hylas Companion",
    "url": "https://hylascompanion.com",
  },
};

const CATEGORY_COLORS = {
  "Pay & Earnings": "text-primary bg-primary/10",
  "Lifestyle":      "text-blue-400 bg-blue-400/10",
  "Business":       "text-orange-400 bg-orange-400/10",
  "Getting Started":"text-green-400 bg-green-400/10",
};

export default function Blog() {
  return (
    <div data-testid="blog-page">
      <SEOHead
        title="Driver Resources & Trucking Tips"
        description="CDL-A driver guides from Hylas Companion: flatbed vs reefer pay, OTR vs local, lease purchase costs, CDL requirements, and pay package breakdowns. Real numbers, no fluff."
        keywords="truck driver tips, CDL-A guide, flatbed vs reefer pay, OTR vs local driving, lease purchase trucking, truck driver pay explained"
        path="/blog"
        schema={BLOG_SCHEMA}
      />

      {/* Hero */}
      <section className="bg-[#0A0A0A] pt-36 pb-16 md:pt-44 md:pb-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="font-head text-xs uppercase tracking-[0.4em] text-primary">
            Driver Resources
          </span>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-[0.1em] text-white mt-4 leading-none">
            Trucking Guides<br />
            <span className="text-primary">No Fluff</span>
          </h1>
          <p className="mt-6 text-white/60 text-lg max-w-xl leading-relaxed">
            Real pay numbers, honest pros and cons, and everything you need to make the right career decision as a CDL-A driver.
          </p>
        </div>
      </section>

      {/* Post grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BLOG_POSTS.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group flex flex-col border border-border bg-[#FAFAFA] hover:border-[#0A0A0A] hover:shadow-lg transition-all duration-300"
              >
                {/* Hero thumbnail */}
                {post.heroImage && (
                  <div className="overflow-hidden h-48 bg-[#0A0A0A]">
                    <img
                      src={post.heroImage}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}

                {/* Category + meta */}
                <div className="px-6 pt-5 pb-3 flex items-center justify-between">
                  <span className={`font-head text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-sm ${CATEGORY_COLORS[post.category] || "text-white/60 bg-white/10"}`}>
                    {post.category}
                  </span>
                  <div className="flex items-center gap-1 text-foreground/40">
                    <Clock className="w-3 h-3" />
                    <span className="text-xs">{post.readTime}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 pb-6 flex flex-col flex-1">
                  <h2 className="font-head text-lg font-bold uppercase tracking-[0.05em] leading-snug text-[#0A0A0A] group-hover:text-primary transition-colors duration-200 mb-3">
                    {post.title}
                  </h2>
                  <p className="text-sm text-foreground/60 leading-relaxed flex-1">
                    {post.excerpt}
                  </p>
                  <div className="mt-5 flex items-center gap-1.5 text-[#0A0A0A] group-hover:text-primary transition-colors duration-200">
                    <span className="font-head text-xs uppercase tracking-[0.2em]">Read Article</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Transfer Market cross-link */}
      <section className="py-14 bg-[#080808] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <span className="font-head text-[9px] uppercase tracking-[0.4em] text-primary/60">Carrier Access</span>
            <h2 className="font-head text-2xl md:text-3xl font-bold uppercase tracking-[0.08em] text-white mt-2">
              Browse Available CDL-A Drivers
            </h2>
            <p className="mt-3 text-white/50 text-sm max-w-lg">
              Pre-screened drivers available now in Illinois, Texas, Georgia, Tennessee, and 6 more states.
              Carriers bid competitively — starting placements from $1,500.
            </p>
          </div>
          <Link
            to="/market"
            className="shrink-0 inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.2em] bg-primary text-black px-8 py-4 hover:brightness-110 transition-all"
          >
            Open the Market <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-primary py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-head text-3xl md:text-4xl font-bold uppercase tracking-[0.1em] text-black">
              Ready to Drive?
            </h2>
            <p className="mt-2 text-black/70">Apply in under 2 minutes. No fees. Nine pay packages.</p>
          </div>
          <Link
            to="/apply"
            className="shrink-0 inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.2em] bg-black text-white px-8 py-4 hover:bg-white hover:text-black transition-colors duration-300"
          >
            Apply Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
